import { all, always, any, assoc, compose, defaultTo, equals, filter, find, ifElse, includes, isEmpty, map, not, pathEq, prop, propEq, reject, toLower, trim, T, isNotNil } from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty, notEqual } from 'ramda-adjunct';
import { ROLES } from '../../../lib/rights';
import { isSuperAdminUserRole, mapToTeamRoles, offerIdIncludes, RoleValueEnum } from './roles';

const defaultToEmptyArray = defaultTo([]);

// Transforms a user object into a standardized format with additional computed properties for roles, teams, and status
export const transformUser = (user, isAdministration, items) => {
  const { id, email, dates, roles: originalRoles, teamRoles: originalTeamRoles, status } = user;
  const roles = defaultToEmptyArray(originalRoles);
  const teamRoles = defaultToEmptyArray(originalTeamRoles);
  const isPending = equals('pending', status);
  const isSuperAdmin = isSuperAdminUserRole(user) || (isPending && pathEq(ROLES.superAdmin, [0, 'roles', 0, 'role'], teamRoles));
  const teams = map(mapToTeamRoles(isAdministration, items, isSuperAdmin), teamRoles);
  const signUp = Number(prop('signUp', dates));
  return {
    id,
    email,
    signUp,
    roles,
    status,
    isPending,
    teams,
    isSuperAdmin
  };
};

// Filter the users list based on email search, selected item, selected role and type of the list (administration or team).
export const filterUsers = (teamsUsers, search, selectedItem, selectedRole, isAdministration) => {
  let users = [...teamsUsers];
  const searchValue = trim(search);

  // Filter by email search if a value is provided.
  if (isNotNilOrEmpty(searchValue)) {
    const searchLower = toLower(searchValue);
    users = filter((user) => includes(searchLower, toLower(prop('email', user))), users);
  }
    
  const superAdminIsSelected = equals(selectedRole, ROLES.superAdmin);

  const itemHasValue = isNotNilOrEmpty(selectedItem);
  const roleHasValue = isNotNilOrEmpty(selectedRole);
  // Filter as soon as an item or a role is selected. The item filter is ignored when
  // super-admin is selected (super-admins are global, not scoped to a team/offer), but
  // the role filter still applies to keep only super-admin users.
  if (itemHasValue || roleHasValue) {
    const isEmptyItemFilter = equals(RoleValueEnum.EMPTY, selectedItem);
    const isEmptyRoleFilter = equals(RoleValueEnum.EMPTY);
    
    const itemFilterFn = itemHasValue && not(superAdminIsSelected) ? compose(
      isEmptyItemFilter
        ? isEmpty
        : itemDefaultFilter(selectedItem, isAdministration),
      prop('teams')
    ): T;
    const roleFilterFn = roleHasValue ? ifElse(
      isEmptyRoleFilter,
      always(roleEmptyFilter),
      always(roleDefaultFilter(selectedRole, selectedItem, isAdministration))
    )(selectedRole): T;

    users = filter((user) => itemFilterFn(user) && roleFilterFn(user), users);
  }
    
  return users;
};

const isEmptyFilterValue = (value) => isNilOrEmpty(value) || equals(RoleValueEnum.EMPTY, value);

export const filterDisplayedRows = (items, { isAdministration, selectedItem, selectedRole }) => {
  if (isEmptyFilterValue(selectedItem) && isEmptyFilterValue(selectedRole)) return items;

  if (isAdministration) {
    const filteredByTeam = isEmptyFilterValue(selectedItem)
      ? items
      : filter(propEq(selectedItem, 'teamId'), items);

    // When super-admin is selected, user-level filtering has already restricted the list to
    // super-admin users only. We keep teamRoles unchanged here because the administration table
    // renders those users through the dedicated `isSuperAdmin` branch instead of team role rows.
    const roleFilterApplies = not(isEmptyFilterValue(selectedRole)) && notEqual(ROLES.superAdmin, selectedRole);
    if (!roleFilterApplies) return filteredByTeam;

    return compose(
      reject(compose(isEmpty, prop('teamRoles'))),
      map((team) => assoc('teamRoles', filter(propEq(selectedRole, 'value'), prop('teamRoles', team)), team))
    )(filteredByTeam);
  }

  const filteredByRole = isEmptyFilterValue(selectedRole)
    ? items
    : filter(propEq(selectedRole, 'role'), items);
  if (isEmptyFilterValue(selectedItem)) return filteredByRole;

  return filter(({ offerId }) => offerIdIncludes(offerId, selectedItem), filteredByRole);
};

// Check if the user belongs to the selected item.
const itemDefaultFilter = (selectedItem, isAdministration) => any((team) => {
  if (isAdministration) {
    const teamId = prop('teamId', team);
    return equals(selectedItem, teamId);
  } else {
    return isNotNil(find(({ role, offerId }) => offerIdIncludes(offerId, selectedItem) || equals(ROLES.admin, role))(prop('roles', team)));
  }
});

// Check if the user has the selected main role or a matching role in one of their teams.
const roleDefaultFilter = (selectedRole, selectedItem, isAdministration) => ({ roles, teams = [] }) => {
  const isInMainRole = includes(selectedRole, roles);
  const hasOfferFilter = isNotNilOrEmpty(selectedItem);
  if (hasOfferFilter) {
    if (isAdministration) {
      teams = filter(propEq(selectedItem, 'teamId'), teams);
    } else {
      teams = filter(compose(any(({ offerId }) => offerIdIncludes(offerId, selectedItem)), prop('roles')), teams);
    }
  }
  // In team mode with an offer filter, the selected role AND the selected offer must
  // match the same role entry (exact combination), otherwise a user holding the role
  // on another offer would be wrongly included.
  const roleMatchesTeam = hasOfferFilter && not(isAdministration)
    ? any((role) => propEq(selectedRole, 'role', role) && offerIdIncludes(prop('offerId', role), selectedItem))
    : any(propEq(selectedRole, 'role'));
  const isInTeamRole = any(compose(roleMatchesTeam, prop('roles')), teams);
  return isInMainRole || isInTeamRole;
};

// Filter users without a main role and without any team role.
const roleEmptyFilter = ({ roles, teams }) => {
  const mainRoleIsEmpty = isEmpty(roles);
  const teamRolesAreEmpty = all(compose(isEmpty, prop('roles')), teams || []);
  return mainRoleIsEmpty && teamRolesAreEmpty;
};